import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Layers, Link2Off, Loader2 } from "lucide-react";
import { api, ApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";

type EnvName = "production" | "staging" | "preview" | "development";

interface LinkedGroup {
  groupId: string;
  name: string;
  position: number;
  vars: Array<{
    key: string;
    environment: EnvName;
    isSecret: boolean;
    /** This project defines its own var of the same key, so the group's loses. */
    overriddenByProject: boolean;
  }>;
}

interface OrgGroup {
  id: string;
  name: string;
}

/**
 * 008 (US2) — the org env groups this project inherits from.
 *
 * Shows provenance rather than just a list: a key the project also defines is
 * marked as overridden, because that is the question someone actually has when
 * a value isn't what they expected.
 */
export function LinkedEnvGroups({
  orgSlug,
  projectSlug,
}: {
  orgSlug: string;
  projectSlug: string;
}) {
  const qc = useQueryClient();
  const base = `/orgs/${orgSlug}/projects/${projectSlug}/env-groups`;

  const linked = useQuery({
    queryKey: ["project-env-groups", orgSlug, projectSlug],
    queryFn: () => api.get<{ groups: LinkedGroup[] }>(base).then((r) => r.groups),
  });
  // Org-wide list, so we can offer what isn't linked yet. A plain member may
  // not be allowed to read this; treat a failure as "nothing to offer".
  const all = useQuery({
    queryKey: ["env-groups", orgSlug],
    queryFn: () =>
      api
        .get<{ groups: OrgGroup[] }>(`/orgs/${orgSlug}/env-groups`)
        .then((r) => r.groups)
        .catch(() => [] as OrgGroup[]),
  });

  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ["project-env-groups", orgSlug, projectSlug] });
  };
  const link = useMutation({
    mutationFn: (groupId: string) => api.post(`${base}/${groupId}`, {}),
    onSuccess: invalidate,
  });
  const unlink = useMutation({
    mutationFn: (groupId: string) => api.delete(`${base}/${groupId}`),
    onSuccess: invalidate,
  });

  if (linked.isLoading) return null;

  const linkedIds = new Set((linked.data ?? []).map((g) => g.groupId));
  const available = (all.data ?? []).filter((g) => !linkedIds.has(g.id));

  return (
    <section className="of-card">
      <div className="of-card-header flex items-center justify-between">
        <h3 className="text-sm font-semibold inline-flex items-center gap-1.5">
          <Layers className="h-3.5 w-3.5" /> Inherited from env groups
        </h3>
        {available.length > 0 && (
          <div className="flex items-center gap-2">
            <select
              className="h-8 px-2 rounded-md border border-border bg-background text-xs"
              defaultValue=""
              onChange={(e) => {
                if (e.target.value) {
                  link.mutate(e.target.value);
                  e.target.value = "";
                }
              }}
            >
              <option value="" disabled>Link a group…</option>
              {available.map((g) => (
                <option key={g.id} value={g.id}>{g.name}</option>
              ))}
            </select>
            {link.isPending && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
          </div>
        )}
      </div>

      {link.error instanceof ApiError && (
        <div className="px-4 py-2 text-xs text-danger">{link.error.message}</div>
      )}

      {(linked.data ?? []).length === 0 ? (
        <div className="px-4 py-6 text-center text-sm text-muted-foreground">
          {available.length > 0
            ? "No groups linked. Link one to share variables across projects."
            : "No env groups defined for this org yet."}
        </div>
      ) : (
        <div className="divide-y divide-border-muted">
          {linked.data!.map((g) => (
            <div key={g.groupId} className="px-4 py-3">
              <div className="flex items-center justify-between gap-2">
                <span className="text-sm font-medium">{g.name}</span>
                <Button
                  variant="ghost"
                  size="sm"
                  title={`Unlink ${g.name}`}
                  onClick={() => {
                    if (window.confirm(`Unlink "${g.name}"? Its variables stop reaching this project's next build.`)) {
                      unlink.mutate(g.groupId);
                    }
                  }}
                >
                  <Link2Off className="h-3.5 w-3.5" /> Unlink
                </Button>
              </div>
              <div className="mt-1.5 flex flex-wrap gap-1.5">
                {g.vars.length === 0 && (
                  <span className="text-xs text-muted-foreground">No variables in this group.</span>
                )}
                {g.vars.map((v) => (
                  <span
                    key={`${v.key}-${v.environment}`}
                    title={
                      v.overriddenByProject
                        ? "This project defines its own value, which wins."
                        : v.environment
                    }
                    className={`text-2xs font-mono rounded px-1.5 py-0.5 border ${
                      v.overriddenByProject
                        ? "bg-subtle text-subtle-foreground border-border-muted line-through opacity-70"
                        : "bg-subtle text-subtle-foreground border-border-muted"
                    }`}
                  >
                    {v.key}
                    <span className="text-subtle-foreground">:{v.environment.slice(0, 4)}</span>
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
